import React, { useState } from 'react'
import {
    Box,
    Button,
    MenuItem,
    Select,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'

import { useGuestCards } from '../../hooks/Outreach/useGuestCards'
import { CreateGuestCardModal } from '../Outbound/Campaign/GuestCards/CreateGuestCardModal'
import { FormSaveAction } from './FormSaveButton'
import {
    GuestCardChannelBindings,
    emptyGuestCardChannelBindings,
} from './seasonConfigBundle'
import { SetupArtifactShell } from './SetupArtifactShell'
import {
    GUEST_CARD_SLOTS,
    MemberConfiguredMap,
    SelectedSetupConfig,
    SetupConfigOption,
    countConfigured,
    emptyMemberMap,
    requiredSlotsReady,
} from './setupArtifacts'

export type { GuestCardChannelBindings }
export { emptyGuestCardChannelBindings }

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        width: '100%',
        padding: theme.spacing(0.5, 0, 1),
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    intro: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
        flex: 1,
        minWidth: 0,
    },
    createButton: {
        flexShrink: 0,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.8125rem',
        whiteSpace: 'nowrap',
    },
    summary: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    card: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
    },
    cardMain: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.25),
        minWidth: 0,
        flex: 1,
    },
    statusOk: {
        color: '#2e7d32',
        fontSize: 22,
        marginTop: 2,
        flexShrink: 0,
    },
    statusMissing: {
        color: theme.palette.text.disabled,
        fontSize: 22,
        marginTop: 2,
        flexShrink: 0,
    },
    title: {
        fontWeight: 600,
        fontSize: '0.9375rem',
    },
    badge: {
        display: 'inline-block',
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(0.75),
    },
    subtitle: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
        lineHeight: 1.4,
    },
    select: {
        minWidth: 220,
        maxWidth: 280,
        flexShrink: 0,
        fontSize: '0.8125rem',
        marginTop: 2,
    },
}))

export function channelBindingsToConfigured(
    bindings: GuestCardChannelBindings,
): MemberConfiguredMap {
    const members = emptyMemberMap(GUEST_CARD_SLOTS)
    for (const slot of GUEST_CARD_SLOTS) {
        members[slot.id] = bindings[slot.id] != null && bindings[slot.id] !== ''
    }
    return members
}

export const GuestCardPackTab: React.FC<{
    selected: SelectedSetupConfig | null
    onSelected: (config: SelectedSetupConfig) => void
    onClearSelection: () => void
    existing: SetupConfigOption[]
    onExistingChange: (next: SetupConfigOption[]) => void
    channelBindings: GuestCardChannelBindings
    onChannelBindingsChange: (next: GuestCardChannelBindings) => void
    contentLocked?: boolean
    /** False when the pinned pack is locked by an active season (fork to edit). */
    artifactEditable?: boolean
    saveAction?: FormSaveAction
}> = ({
    selected,
    onSelected,
    onClearSelection,
    existing,
    onExistingChange,
    channelBindings,
    onChannelBindingsChange,
    contentLocked = false,
    artifactEditable = true,
    saveAction,
}) => {
    const classes = useStyles()
    const readOnly =
        !!selected?.orgMandated || contentLocked || !artifactEditable
    const members = channelBindingsToConfigured(channelBindings)
    const counts = countConfigured(GUEST_CARD_SLOTS, members)
    const ready = requiredSlotsReady(GUEST_CARD_SLOTS, members)
    const { guestCards, isLoadingGuestCards } = useGuestCards()
    const cards = (guestCards ?? []).filter((card) => !card.archived)
    const [createOpen, setCreateOpen] = useState(false)

    const handleGuestCardCreated = (guestCardId: number) => {
        setCreateOpen(false)
        const id = String(guestCardId)
        const emptySlot = GUEST_CARD_SLOTS.find((slot) => {
            const assigned = channelBindings[slot.id]
            return assigned == null || assigned === ''
        })
        if (emptySlot && !readOnly) {
            onChannelBindingsChange({
                ...channelBindings,
                [emptySlot.id]: id,
            })
        }
    }

    return (
        <SetupArtifactShell
            kind="guest_card"
            selected={selected}
            onSelected={onSelected}
            onClearSelection={onClearSelection}
            existing={existing}
            onExistingChange={onExistingChange}
            contentLocked={contentLocked}
            readinessLabel={
                ready
                    ? 'Required slots ready'
                    : `${counts.requiredReady}/${counts.required} required`
            }
            saveAction={readOnly ? undefined : saveAction}
        >
            <Box className={classes.root}>
                <Box className={classes.headerRow}>
                    <Typography className={classes.intro}>
                        Assign a guest card to each intake channel. Walk-in uses
                        the selected card directly. Campaign and website use
                        theirs as defaults — you can still pick a different card
                        when building campaigns or managing website links.
                    </Typography>
                    {!readOnly && (
                        <Button
                            className={classes.createButton}
                            color="primary"
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateOpen(true)}
                        >
                            Create Guest Card
                        </Button>
                    )}
                </Box>
                <Typography className={classes.summary}>
                    {ready
                        ? 'Required slots ready'
                        : `${counts.requiredReady}/${counts.required} required`}
                </Typography>
                {GUEST_CARD_SLOTS.map((slot) => {
                    const assigned = channelBindings[slot.id]
                    const isConfigured = assigned != null && assigned !== ''
                    return (
                        <Box key={slot.id} className={classes.card}>
                            <Box className={classes.cardMain}>
                                {isConfigured ? (
                                    <CheckCircleOutlineIcon
                                        className={classes.statusOk}
                                    />
                                ) : (
                                    <RadioButtonUncheckedIcon
                                        className={classes.statusMissing}
                                    />
                                )}
                                <Box style={{ minWidth: 0 }}>
                                    <Typography className={classes.title}>
                                        {slot.label}
                                        <span className={classes.badge}>
                                            {slot.comingSoon
                                                ? 'Coming soon'
                                                : slot.required
                                                ? 'Required'
                                                : 'Optional'}
                                        </span>
                                    </Typography>
                                    <Typography className={classes.subtitle}>
                                        {slot.description}
                                    </Typography>
                                </Box>
                            </Box>
                            <Select
                                className={classes.select}
                                value={assigned ?? ''}
                                disabled={readOnly || isLoadingGuestCards}
                                variant="outlined"
                                margin="dense"
                                displayEmpty
                                onChange={(e) => {
                                    const value = String(e.target.value)
                                    onChannelBindingsChange({
                                        ...channelBindings,
                                        [slot.id]: value === '' ? null : value,
                                    })
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select guest card…</em>
                                </MenuItem>
                                {cards.map((card) => (
                                    <MenuItem
                                        key={card.id}
                                        value={String(card.id)}
                                    >
                                        {card.card_name || `Card #${card.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )
                })}
            </Box>
            <CreateGuestCardModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onGuestCardCreated={handleGuestCardCreated}
            />
        </SetupArtifactShell>
    )
}
